"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Calendar, Users, Star, Phone, Mail, User, UserCheck, Trophy, Medal } from "lucide-react"
import type { Fencer, AttendanceRecord, CompetitionResult } from "../types"
import { api } from "../services/api"
import { calculateAge, isAdult, getSeasonsList, getCurrentSeason } from "../utils/dateUtils"

export const FencerDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [fencer, setFencer] = useState<Fencer | null>(null)
  const [attendanceHistory, setAttendanceHistory] = useState<AttendanceRecord[]>([])
  const [competitionResults, setCompetitionResults] = useState<
    (CompetitionResult & { competitionName: string; date: string })[]
  >([])
  const [selectedSeason, setSelectedSeason] = useState(getCurrentSeason().name)
  const [loading, setLoading] = useState(true)

  const seasons = getSeasonsList()

  useEffect(() => {
    if (id) {
      loadFencerDetails(id)
    }
  }, [id])

  // Update the loadData function to properly fetch competition results
  const loadFencerDetails = async (fencerId: string) => {
    try {
      const [fencersData, allAttendance, allCompetitions, allResults] = await Promise.all([
        api.getFencers(),
        api.getAttendanceRecords(),
        api.getCompetitions(),
        api.getCompetitionResults(undefined, fencerId),
      ])

      const fencerData = fencersData.find((f) => f.id === fencerId)
      if (!fencerData) {
        navigate("/fencers")
        return
      }

      setFencer(fencerData)

      // Filter attendance for this fencer and sort by date (newest first)
      const fencerAttendance = allAttendance
        .filter((record) => record.fencerId === fencerId && record.present)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

      setAttendanceHistory(fencerAttendance)

      // Get competition data for each result
      const fencerResults = allResults
        .map((result) => {
          const competition = allCompetitions.find((c) => c.id === result.competitionId)
          return {
            ...result,
            competitionName: competition?.name || "Unknown Competition",
            date: competition?.date || "Unknown Date",
          }
        })
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

      setCompetitionResults(fencerResults as any)
    } catch (error) {
      console.error("Error loading fencer details:", error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading fencer details...</span>
      </div>
    )
  }

  if (!fencer) {
    return (
      <div className="text-center py-12">
        <User className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Fencer not found</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">The requested fencer could not be found.</p>
        <button
          onClick={() => navigate("/fencers")}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          Back to Fencers
        </button>
      </div>
    )
  }

  const age = calculateAge(fencer.dateOfBirth)
  const adult = isAdult(fencer.dateOfBirth)

  const trainingHistory = attendanceHistory.filter((record) => record.present)
  const privateLessonHistory = attendanceHistory.filter((record) => record.privateLessonReceived)

  const lastTrainingDate = trainingHistory.length > 0 ? trainingHistory[0].date : null

  // Update the seasonResults filtering to use the enhanced result objects
  const selectedSeasonData = seasons.find((s) => s.name === selectedSeason)
  const seasonResults = selectedSeasonData
    ? competitionResults.filter(
        (result) => result.date >= selectedSeasonData.startDate && result.date <= selectedSeasonData.endDate,
      )
    : []

  const getPlacementColor = (placement: number, total: number) => {
    const percentage = placement / total
    if (placement === 1) return "text-yellow-600 dark:text-yellow-400" // Gold
    if (placement === 2) return "text-gray-500 dark:text-gray-400" // Silver
    if (placement === 3) return "text-orange-600 dark:text-orange-400" // Bronze
    if (percentage <= 0.1) return "text-green-600 dark:text-green-400" // Top 10%
    if (percentage <= 0.25) return "text-blue-600 dark:text-blue-400" // Top 25%
    return "text-gray-600 dark:text-gray-300"
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <button
          onClick={() => navigate("/fencers")}
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors duration-200"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Fencers
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Fencer Information */}
        <div className="lg:col-span-1">
          <div className="card p-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="h-10 w-10 text-blue-600 dark:text-blue-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{fencer.name}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {age} years old • {adult ? "Adult" : "Minor"}
              </p>
            </div>

            <div className="mt-6 space-y-4">
              <div className="flex items-center">
                <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Email</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{fencer.email}</p>
                </div>
              </div>

              <div className="flex items-center">
                <Phone className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Phone</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{fencer.phone}</p>
                </div>
              </div>

              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Date of Birth</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{fencer.dateOfBirth}</p>
                </div>
              </div>

              <div className="flex items-center">
                <UserCheck className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Registration Date</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{fencer.registrationDate}</p>
                </div>
              </div>

              {!adult && fencer.guardians.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Guardians</h3>
                  <div className="space-y-3">
                    {fencer.guardians.map((guardian) => (
                      <div key={guardian.id} className="space-y-2">
                        <div className="flex items-center">
                          <User className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                          <div>
                            <p className="text-sm text-gray-900 dark:text-white">{guardian.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {guardian.relationship || "Guardian"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">{guardian.phone}</p>
                        </div>
                        {guardian.email && (
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">{guardian.email}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="card p-6 mt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Quick Stats</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-blue-500 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Training Sessions</span>
                </div>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">{trainingHistory.length}</span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <Star className="h-5 w-5 text-yellow-500 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Private Lessons</span>
                </div>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {privateLessonHistory.length}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <Trophy className="h-5 w-5 text-purple-500 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-300">Competitions</span>
                </div>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">{competitionResults.length}</span>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Last Training</span>
                  <span className="text-sm text-blue-600 dark:text-blue-400">{lastTrainingDate || "Never"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Competition Results and Attendance History */}
        <div className="lg:col-span-2 space-y-6">
          {/* Competition Results by Season */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Competition Results</h2>
              <select
                value={selectedSeason}
                onChange={(e) => setSelectedSeason(e.target.value)}
                className="rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:focus:border-blue-400 dark:focus:ring-blue-400 px-3 py-1 text-sm"
              >
                {seasons.map((season) => (
                  <option key={season.name} value={season.name}>
                    Season {season.name}
                  </option>
                ))}
              </select>
            </div>

            {seasonResults.length > 0 ? (
              <div className="space-y-4">
                {seasonResults.map((result) => {
                  const placementColor = getPlacementColor(result.placement, result.totalParticipants)

                  return (
                    <div
                      key={result.id}
                      className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex items-center">
                        <Trophy className="h-5 w-5 text-purple-500 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{result.competitionName}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {result.date} • {result.ageCategory}
                          </p>
                          {result.notes && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{result.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {result.placement <= 3 && <Medal className={`h-4 w-4 ${placementColor}`} />}
                        <span className={`text-sm font-semibold ${placementColor}`}>
                          {result.placement} / {result.totalParticipants}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Trophy className="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No competitions this season</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  No competition results found for season {selectedSeason}.
                </p>
              </div>
            )}
          </div>

          {/* Attendance History */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Attendance</h2>
              {lastTrainingDate && (
                <div className="text-sm text-gray-500 dark:text-gray-400">Last training: {lastTrainingDate}</div>
              )}
            </div>

            {attendanceHistory.length > 0 ? (
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {attendanceHistory.slice(0, 20).map((record) => (
                  <div
                    key={record.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div className="flex items-center">
                      <Users className="h-5 w-5 text-blue-500 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Training Session</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{record.date}</p>
                        {record.notes && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{record.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        Present
                      </span>
                      {record.privateLessonReceived && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                          <Star className="h-3 w-3 mr-1" />
                          Private Lesson
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
                <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No attendance history</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  This fencer hasn't attended any sessions yet.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
